import ThemeToast from '@theme/lib-toast';

const SUCCESS_CLASS = 'newsletter-signup-block__message--success';
const ERROR_CLASS = 'newsletter-signup-block__message--error';

/** @type {WeakMap<HTMLFormElement, AbortController>} */
const formControllers = new WeakMap();

/**
 * @param {string} html
 * @returns {string}
 */
function decodeHtml(html) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

/**
 * @param {HTMLFormElement} form
 * @returns {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]}
 */
function getValidatableFields(form) {
  /** @type {(HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)[]} */
  const fields = [];

  form.querySelectorAll('input, textarea, select').forEach((field) => {
    if (
      field instanceof HTMLInputElement ||
      field instanceof HTMLTextAreaElement ||
      field instanceof HTMLSelectElement
    ) {
      if (field.disabled) return;
      if (
        field instanceof HTMLInputElement &&
        (field.type === 'hidden' ||
          field.type === 'submit' ||
          field.type === 'button' ||
          field.type === 'reset')
      ) {
        return;
      }
      fields.push(field);
    }
  });

  return fields;
}

/**
 * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function isFieldEmpty(field, form) {
  if (field instanceof HTMLInputElement) {
    if (field.type === 'checkbox' || field.type === 'radio') {
      if (!field.required) return false;
      const group = form.querySelectorAll(`input[name="${field.name}"]`);
      return !Array.from(group).some((input) => {
        return input instanceof HTMLInputElement && input.checked;
      });
    }
  }

  return field.value.trim().length === 0;
}

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function updateSubmitState(form) {
  const submitButton = form.querySelector('[type="submit"]');
  if (!(submitButton instanceof HTMLButtonElement)) {
    return true;
  }

  const invalidField = getValidatableFields(form).find((field) => {
    if (!field.required) return false;
    return isFieldEmpty(field, form);
  });

  submitButton.disabled = Boolean(invalidField);
  return !invalidField;
}

/**
 * @param {'success' | 'error'} type
 * @param {string} message
 */
function dispatchToast(type, message) {
  if (!message) return;

  if (type === 'success') {
    ThemeToast.success(message);
    return;
  }

  ThemeToast.error(message);
}

/**
 * @param {HTMLFormElement} form
 * @returns {{
 *   testMode: boolean;
 *   successMessage: string;
 *   errorMessage: string;
 *   testModeMessage: string;
 * }}
 */
function getFormConfig(form) {
  const root = form.closest('[data-newsletter-signup-block]');
  const dataset = root instanceof HTMLElement ? root.dataset : form.dataset;

  return {
    testMode: dataset.testMode === 'true',
    successMessage: decodeHtml(dataset.successMessage || ''),
    errorMessage: decodeHtml(dataset.errorMessage || ''),
    testModeMessage: decodeHtml(dataset.testMessage || ''),
  };
}

/**
 * Remove Shopify's contact_posted query param after a native redirect.
 */
function cleanContactPostedUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('contact_posted')) return;

  url.searchParams.delete('contact_posted');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
}

/**
 * Show toasts from Liquid success/error markup after a native POST redirect.
 * @param {HTMLFormElement} form
 */
function maybeToastPostedResult(form) {
  const config = getFormConfig(form);
  const root = form.closest('[data-newsletter-signup-block]') || form;
  const successElement = root.querySelector(`.${SUCCESS_CLASS}`);
  const errorElement = root.querySelector(`.${ERROR_CLASS}`);

  if (successElement) {
    dispatchToast(
      'success',
      successElement.textContent?.trim() || config.successMessage
    );
    cleanContactPostedUrl();
    return;
  }

  if (errorElement) {
    dispatchToast(
      'error',
      errorElement.textContent?.trim() || config.errorMessage
    );
  }
}

/**
 * @param {HTMLFormElement} form
 */
function initNewsletterSignupForm(form) {
  const existingController = formControllers.get(form);
  existingController?.abort();

  const abortController = new AbortController();
  const { signal } = abortController;
  formControllers.set(form, abortController);

  const onSubmit = (/** @type {SubmitEvent} */ event) => {
    if (!updateSubmitState(form)) {
      event.preventDefault();
      return;
    }

    const config = getFormConfig(form);

    if (config.testMode || window.Shopify?.designMode) {
      event.preventDefault();
      dispatchToast(
        'success',
        config.testMode ? config.testModeMessage : config.successMessage
      );
      form.reset();
      updateSubmitState(form);
    }
  };

  const onFieldChange = () => {
    updateSubmitState(form);
  };

  form.addEventListener('submit', onSubmit, { signal });
  form.addEventListener('input', onFieldChange, { signal });
  form.addEventListener('change', onFieldChange, { signal });
  form.addEventListener(
    'reset',
    () => {
      requestAnimationFrame(() => {
        updateSubmitState(form);
      });
    },
    { signal }
  );

  updateSubmitState(form);
  maybeToastPostedResult(form);
}

/**
 * @param {ParentNode} [root]
 */
function initNewsletterSignupForms(root = document) {
  root
    .querySelectorAll('[data-newsletter-signup-block] form')
    .forEach((form) => {
      if (form instanceof HTMLFormElement) {
        initNewsletterSignupForm(form);
      }
    });
}

initNewsletterSignupForms(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    initNewsletterSignupForms(event.target);
  }
});
